import { createAsyncThunk } from '@reduxjs/toolkit';
import { chatScriptService } from '../../../services/chat-script.service';
import { ScriptCategoryDto, ScriptDto } from '../../../helpers/types';
import { AxiosError } from 'axios';

export const fetchScriptCategoriesByCreator = createAsyncThunk<{ creatorId: string; categories: ScriptCategoryDto[] }, string, { rejectValue: { message: string } }>(
  'script/fetchCategoriesByCreator',
  async (creatorId, { rejectWithValue }) => {
    try {
      const categories = await chatScriptService.getCategories(creatorId);
      return { creatorId, categories };
    } catch (err) {
      const error = err as AxiosError<any>;
      return rejectWithValue({ message: (error.response?.data as any)?.message || 'Failed to load categories' });
    }
  }
);

export const createScriptCategoryAction = createAsyncThunk<
  { creatorId: string; category: ScriptCategoryDto },
  { creatorId: string; name: string },
  { rejectValue: { message: string } }
>('script/createCategory', async ({ creatorId, name }, { rejectWithValue }) => {
  try {
    const category = await chatScriptService.createCategory({ name, creatorId });
    return { creatorId, category };
  } catch (err) {
    const error = err as AxiosError<any>;
    return rejectWithValue({ message: (error.response?.data as any)?.message || 'Failed to create category' });
  }
});

export const updateScriptCategoryAction = createAsyncThunk<{ id: string; name: string }, { id: string; name: string }, { rejectValue: { message: string } }>(
  'script/updateCategory',
  async ({ id, name }, { rejectWithValue }) => {
    try {
      await chatScriptService.updateCategory(id, { name });
      return { id, name };
    } catch (err) {
      const error = err as AxiosError<any>;
      return rejectWithValue({ message: (error.response?.data as any)?.message || 'Failed to update category' });
    }
  }
);

export const deleteScriptCategoryAction = createAsyncThunk<{ id: string }, string, { rejectValue: { message: string; status?: number } }>(
  'script/deleteCategory',
  async (id, { rejectWithValue }) => {
    try {
      const success = await chatScriptService.deleteCategory(id);
      if (!success) throw new Error('Delete failed');
      return { id };
    } catch (err) {
      const error = err as AxiosError<any>;
      return rejectWithValue({ message: (error.response?.data as any)?.message || 'Failed to delete category', status: error.response?.status });
    }
  }
);

export const fetchScriptsByFilter = createAsyncThunk<
  { key: string; page: number; items: ScriptDto[]; total: number; totalPages: number },
  { creatorId: string; filterOrCategoryId: string; page?: number },
  { rejectValue: { message: string } }
>('script/fetchScriptsByFilter', async ({ creatorId, filterOrCategoryId, page = 1 }, { rejectWithValue }) => {
  try {
    const res = await chatScriptService.getScripts({ creatorId, filterOrCategoryId, page });
    const key = `${creatorId}__${filterOrCategoryId}`;
    return { key, page, items: res.data, total: res.total, totalPages: res.totalPages };
  } catch (err) {
    const error = err as AxiosError<any>;
    return rejectWithValue({ message: (error.response?.data as any)?.message || 'Failed to load scripts' });
  }
});
